import React, { useState } from 'react';
import axios from 'axios';
import { API_BASE_URL, NGROK_URL } from '../../utils/const';

const maritialStatusToRebateIncome={
    single:'32,279',
    marriedApart:'31,279',
    married:'28,974'
}

const formSchema = {
    incomeYear: {
      fields: [
        {
          type: 'select',
          name: 'incomeYear', // Corresponds to target_income_year
          label: 'Income Year *',
          required: true,
          options: [
            { value: '', label: 'Select Income Year' },
            { value: '2023', label: '2022-23' }, // Ensure these values match backend (e.g., "2023" for "2022-23")
            { value: '2024', label: '2023-24' }  // Ensure these values match backend (e.g., "2024" for "2023-24")
          ]
        }
      ]
    },
  
    saptoEligible: { // Renamed from pensionEligiblity; Corresponds to target_sapto_eligible
      condition: (formData) => formData.incomeYear !== undefined && formData.incomeYear !== '',
      fields: [
        {
          type: 'radio',
          name: 'saptoEligible',
          label: 'Were you eligible for an Australian Government age pension or similar type of payment (e.g. Seniors and Pensioners Tax Offset - SAPTO) during the selected income year? *',
          required: true,
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' }
          ]
        }
      ]
    },
  
    // ---- SAPTO YES PATH ----
    maritalStatus: { // Corresponds to target_marital_status
      condition: (formData) => formData.saptoEligible === 'yes',
      fields: [
        {
          type: 'radio',
          name: 'maritalStatus',
          label: 'What is your marital status? *',
          required: true,
          options: [
            { value: 'single', label: 'Single, widowed or separated at any time during the year' }, // Backend uses "vrb-maritalStatus{target_marital_status}"
            { value: 'marriedApart', label: 'Have a spouse but lived apart due to illness' }, // Ensure these values align
            { value: 'married', label: 'Lived with your spouse for the full year' } // Ensure these values align
          ]
        }
      ]
    },
  
    overRebateIncomeLimit: { // Corresponds to target_over_rebate_limit
      condition: (formData) => formData.saptoEligible === 'yes' &&
                             formData.maritalStatus !== undefined && formData.maritalStatus !== '',
      fields: [
        {
          type: 'radio',
          name: 'overRebateIncomeLimit',
          label: (formData) => (`Was your rebate income (not including your spouse's) more than $${maritialStatusToRebateIncome[formData.maritalStatus]} during the ${formData.incomeYear} income year? *`),
          required: true,
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' }
          ]
        }
      ]
    },

    taxWithheld: { // Corresponds to target_tax_withheld
      condition: (formData) =>
        // SAPTO=YES path: after overRebateIncomeLimit is answered
      (formData.saptoEligible === 'yes' && formData.overRebateIncomeLimit === 'no'),
      fields: [
        {
          type: 'radio',
          name: 'taxWithheld',
          label: 'Did you have tax withheld from any payments made to you during the income year (e.g., from salary, wages, investment income)? *',
          required: true,
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' }
          ]
        }
      ]
    },
    paygInstalment: { // Corresponds to target_payg_instalment
        condition: (formData) => formData.taxWithheld === 'no', // Appears if taxWithheld was visible and answered 'no'
        fields: [
          {
            type: 'radio',
            name: 'paygInstalment',
            label: 'Did you make Pay As You Go (PAYG) installments during the income year? *',
            required: true,
            options: [
              { value: 'yes', label: 'Yes' },
              { value: 'no', label: 'No' }
            ]
          }
        ]
      },
    businessEvents: { // Corresponds to target_other_events (array of values e.g. "1","2","3")
        condition: (formData) => formData.paygInstalment === 'no',
        fields: [
          {
            type: 'checkboxGroup',
            name: 'businessEvents',
            label: 'Select the business or investment events that applied to you during the 2021-22 income year *',
            required: true, // As per original schema
            options: [
                { value: '1', label: 'You carried on a business' },
                { value: '2', label: 'You received income from dividends or distributions that exceeded the threshold amount of $18,200.' },
                { value: '3', label: 'The threshold amount is $416 if you were under 18 years of age at the end of the financial year and were entitled to a franking credit.' },
                { value: '4', label: 'You made a loss (including a capital or a non-capital loss on redemption of a traditional security) or you can claim such a loss you made in a previous year' },
                { value: '5', label: 'You were entitled to a distribution from a trust or a partnership that carried on a primary production business' },
                { value: '6', label: 'You qualified as a special professional (sportsperson, artist, inventor) covered by the income averaging rules' },
                { value: 'none', label: 'None of the above' }
      ]
          }
        ]
      },
      superEvents: { // Corresponds to target_super_events (array of values e.g. "1","2","3")
        condition: (formData) => formData.businessEvents.includes('none'),
        fields: [
          {
            type: 'checkboxGroup',
            name: 'superEvents',
            label: 'Did any of the following superannuation related events occur? (Select all that apply)',
            required: true, // As per original schema
            options: [
        { 
          value: '1', 
          label: 'Your concessional contributions to your super exceeded your concessional contributions cap' 
        },
        { 
          value: '2', 
          label: 'Your non-concessional contributions to your super exceeded your non-concessional contributions cap' 
        },
        { 
          value: '3', 
          label: 'You made personal contributions (not including amounts which you are claiming as a deduction) to a complying super fund or retirement savings account and will be eligible to receive a super co-contribution for these contributions',
          tooltip: 'A complying super fund needs to be an Australian super fund at all times during the financial year.'
        },
        { 
          value: '4', 
          label: 'You received an Australian superannuation lump sum that included an untaxed element or it is a superannuation lump sum death benefit paid to you as a non-dependant and you were 60 years old or older' 
        },
        { 
          value: '5', 
          label: 'You received an Australian superannuation lump sum that included a taxed element or untaxed element or it is a superannuation lump sum death benefit paid to you as a non-dependant and you were under 60 years old' 
        },
        { 
          value: 'none', 
          label: 'None of the above' 
        }
      ]
          }
        ]
      },

      otherEvents: { // Corresponds to target_other_events (array of values e.g. "1","2","3")
      condition: (formData) => formData.superEvents.includes('none'),
      fields: [
        {
          type: 'checkboxGroup',
          name: 'otherEvents',
          label: 'Did any of these other specific circumstances apply to you? (Select all that apply)',
          required: true, // As per original schema
          options: [
            { 
              value: '1', 
              label: 'You received a payment summary or an income statement with a reportable fringe benefits amount' 
            },
            { 
              value: '2', 
              label: 'You received a payment summary or an income statement with a reportable employer superannuation contributions amount' 
            },
            { 
              value: '3', 
              label: 'You were entitled to the private health insurance rebate but you did not claim your correct entitlement as a premium reduction, and your spouse (if you had one) is not claiming the rebate for you in their income tax return' 
            },
            { 
              value: '4', 
              label: 'You are entitled to a credit for the foreign resident capital gains withholding paid by the purchaser to the ATO when you sold the property, share or unit' 
            },
            { 
              value: '5', 
              label: 'You were an Australian resident for tax purposes with exempt foreign employment income and $1 or more of other income' 
            },
            { 
              value: 'none', 
              label: 'None of the above' 
            }
          ]
        }
      ]
    },
 foreignResidentHelp: {
  condition: (formData) => formData.otherEvents.includes('none'),
  fields: [
    {
      type: 'radio',
      name: 'foreignResidentHelp',
      label: (formData)=> `Did all of the following circumstances apply to you in the ${formData.incomeYear} income year? *`,
      subLabel: [
        'You were a foreign resident for tax purposes (full or part year)',
        'You had a Higher Education Loan Program, VET Student Loan or Australian Apprenticeship Support Loan debt, and',
        'Your worldwide income was above $11,753'
      ],
    //   tooltip: 'Your worldwide income includes income from all sources, both in Australia and overseas.',
      required: true,
      options: [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' }
      ]
    }
  ]
},
entitledToChildSupport: { // Corresponds to target_payg_instalment
        condition: (formData) => formData.foreignResidentHelp === 'no', // Appears if taxWithheld was visible and answered 'no'
        fields: [
          {
            type: 'radio',
            name: 'entitledToChildSupport',
            label: (formData)=>`Were you a liable parent or a parent entitled to child support under a child support assessment during the ${formData.incomeYear} income year? *`,
            required: true,
            options: [
              { value: 'yes', label: 'Yes' },
              { value: 'no', label: 'No' }
            ]
          }
        ]
      },

      receivedPension: { // Corresponds to target_payg_instalment
        condition: (formData) => formData.entitledToChildSupport === 'yes', // Appears if taxWithheld was visible and answered 'no'
        fields: [
          {
            type: 'radio',
            name: 'receivedPension',
            label: (formData)=>`Were you a liable parent or a parent entitled to child support under a child support assessment during the ${formData.incomeYear} income year? *`,
            required: true,
            options: [
              { value: 'yes', label: 'Yes' },
              { value: 'no', label: 'No' }
            ]
          }
        ]
      },
      // Replace the totalMoreThanThreshold placeholder with:
totalMoreThanThreshold: {
    condition: (formData) => formData.receivedPension === 'yes',
    fields: [
      {
        type: 'radio',
        name: 'totalMoreThanThreshold',
        label: (formData) => `Was the total of all the following payments that you received more than $27,063 for the ${formData.incomeYear} income year? *`,
        subLabel: [
          'taxable income (excluding any assessable First home super saver released amount)',
          'exempt Australian Government allowances, pensions and payments',
          'target foreign income',
          'reportable fringe benefits total',
          'total net investment loss, and',
          'reportable superannuation contributions'
        ],
        required: true,
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' }
        ]
      }
    ]
  },

    // ---- SAPTO NO PATH ----
    govAllowance: { // Renamed from governmentAllowance; Corresponds to target_gov_allowance
      condition: (formData) => formData.saptoEligible === 'no',
      fields: [
        {
          type: 'radio',
          name: 'govAllowance',
          label: 'Did you receive any Australian Government allowance or payment (e.g. JobSeeker, Youth Allowance, Austudy)? *',
          required: true,
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' }
          ]
        }
      ]
    },
    incomeAboveThreshold: { // Renamed from governmentAllowance; Corresponds to target_gov_allowance
      condition: (formData) => formData.saptoEligible === 'no' && formData.govAllowance==='yes',
      fields: [
        {
          type: 'radio',
          name: 'incomeAboveThreshold',
          label:(formData)=> `Was your taxable income more than $21,884 during the ${formData.incomeYear} income year? * `,
          required: true,
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' }
          ]
        }
      ]
    },

    taxWithheldSaptoNo: { // Corresponds to target_tax_withheld
      condition: (formData) =>
        // SAPTO=YES path: after overRebateIncomeLimit is answered
      (formData.saptoEligible === 'no' && formData.incomeAboveThreshold === 'no') ,
      fields: [
        {
          type: 'radio',
          name: 'taxWithheldSaptoNo',
          label: 'Did you have tax withheld from any payments made to you during the income year (e.g., from salary, wages, investment income)? *',
          required: true,
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' }
          ]
        }
      ]
    },
    paygInstalmentSaptoNo: { // Corresponds to target_payg_instalment
      condition: (formData) => formData.saptoEligible==='no'&& formData.taxWithheldSaptoNo === 'no', // Appears if taxWithheld was visible and answered 'no'
      fields: [
        {
          type: 'radio',
          name: 'paygInstalmentSaptoNo',
          label: 'Did you make Pay As You Go (PAYG) installments during the income year? *',
          required: true,
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' }
          ]
        }
      ]
    },
  businessEventsSaptoNo: { // Corresponds to target_other_events (array of values e.g. "1","2","3")
      condition: (formData) => formData.paygInstalmentSaptoNo === 'no',
      fields: [
        {
          type: 'checkboxGroup',
          name: 'businessEventsSaptoNo',
          label: 'Select the business or investment events that applied to you during the 2021-22 income year *',
          required: true, // As per original schema
          options: [
              { value: '1', label: 'You carried on a business' },
              { value: '2', label: 'You received income from dividends or distributions that exceeded the threshold amount of $18,200.' },
              { value: '3', label: 'The threshold amount is $416 if you were under 18 years of age at the end of the financial year and were entitled to a franking credit.' },
              { value: '4', label: 'You made a loss (including a capital or a non-capital loss on redemption of a traditional security) or you can claim such a loss you made in a previous year' },
              { value: '5', label: 'You were entitled to a distribution from a trust or a partnership that carried on a primary production business' },
              { value: '6', label: 'You qualified as a special professional (sportsperson, artist, inventor) covered by the income averaging rules' },
              { value: 'none', label: 'None of the above' }
    ]
        }
      ]
    },
    superEventsSaptoNo: { // Corresponds to target_super_events (array of values e.g. "1","2","3")
      condition: (formData) => formData.businessEventsSaptoNo.includes('none'),
      fields: [
        {
          type: 'checkboxGroup',
          name: 'superEventsSaptoNo',
          label: 'Did any of the following superannuation related events occur? (Select all that apply)',
          required: true, // As per original schema
          options: [
      { 
        value: '1', 
        label: 'Your concessional contributions to your super exceeded your concessional contributions cap' 
      },
      { 
        value: '2', 
        label: 'Your non-concessional contributions to your super exceeded your non-concessional contributions cap' 
      },
      { 
        value: '3', 
        label: 'You made personal contributions (not including amounts which you are claiming as a deduction) to a complying super fund or retirement savings account and will be eligible to receive a super co-contribution for these contributions',
        tooltip: 'A complying super fund needs to be an Australian super fund at all times during the financial year.'
      },
      { 
        value: '4', 
        label: 'You received an Australian superannuation lump sum that included an untaxed element or it is a superannuation lump sum death benefit paid to you as a non-dependant and you were 60 years old or older' 
      },
      { 
        value: '5', 
        label: 'You received an Australian superannuation lump sum that included a taxed element or untaxed element or it is a superannuation lump sum death benefit paid to you as a non-dependant and you were under 60 years old' 
      },
      { 
        value: 'none', 
        label: 'None of the above' 
      }
    ]
        }
      ]
    },

    otherEventsSaptoNo: { // Corresponds to target_other_events (array of values e.g. "1","2","3")
    condition: (formData) => formData.superEventsSaptoNo.includes('none'),
    fields: [
      {
        type: 'checkboxGroup',
        name: 'otherEventsSaptoNo',
        label: 'Did any of these other specific circumstances apply to you? (Select all that apply)',
        required: true, // As per original schema
        options: [
          { 
            value: '1', 
            label: 'You received a payment summary or an income statement with a reportable fringe benefits amount' 
          },
          { 
            value: '2', 
            label: 'You received a payment summary or an income statement with a reportable employer superannuation contributions amount' 
          },
          { 
            value: '3', 
            label: 'You were entitled to the private health insurance rebate but you did not claim your correct entitlement as a premium reduction, and your spouse (if you had one) is not claiming the rebate for you in their income tax return' 
          },
          { 
            value: '4', 
            label: 'You are entitled to a credit for the foreign resident capital gains withholding paid by the purchaser to the ATO when you sold the property, share or unit' 
          },
          { 
            value: '5', 
            label: 'You were an Australian resident for tax purposes with exempt foreign employment income and $1 or more of other income' 
          },
          { 
            value: 'none', 
            label: 'None of the above' 
          }
        ]
      }
    ]
  },
    foreignResidentHelpSaptoNo: {
    condition: (formData) => formData.otherEventsSaptoNo.includes('none'),
    fields: [
      {
        type: 'radio',
        name: 'foreignResidentHelpSaptoNo',
        label: (formData)=> `Did all of the following circumstances apply to you in the ${formData.incomeYear} income year? *`,
        subLabel: [
          'You were a foreign resident for tax purposes (full or part year)',
          'You had a Higher Education Loan Program, VET Student Loan or Australian Apprenticeship Support Loan debt, and',
          'Your worldwide income was above $11,753'
        ],
      //   tooltip: 'Your worldwide income includes income from all sources, both in Australia and overseas.',
        required: true,
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' }
        ]
      }
    ]
    },
    entitledToChildSupportSaptoNo: { // Corresponds to target_payg_instalment
          condition: (formData) => formData.foreignResidentHelpSaptoNo === 'yes', // Appears if taxWithheld was visible and answered 'no'
          fields: [
            {
              type: 'radio',
              name: 'entitledToChildSupportSaptoNo',
              label: (formData)=>`Were you a liable parent or a parent entitled to child support under a child support assessment during the ${formData.incomeYear} income year? *`,
              required: true,
              options: [
                { value: 'yes', label: 'Yes' },
                { value: 'no', label: 'No' }
              ]
            }
          ]
        },

    receivedPensionSaptoNo: { // Corresponds to target_payg_instalment
      condition: (formData) => formData.entitledToChildSupportSaptoNo === 'yes', // Appears if taxWithheld was visible and answered 'no'
      fields: [
        {
          type: 'radio',
          name: 'receivedPensionSaptoNo',
          label: (formData)=>`Were you a liable parent or a parent entitled to child support under a child support assessment during the ${formData.incomeYear} income year? *`,
          required: true,
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' }
          ]
        }
      ]
    },
    // Replace the totalMoreThanThreshold placeholder with:
totalMoreThanThresholdSaptoNo: {
  condition: (formData) => formData.receivedPensionSaptoNo === 'yes',
  fields: [
    {
      type: 'radio',
      name: 'totalMoreThanThreshold',
      label: (formData) => `Was the total of all the following payments that you received more than $27,063 for the ${formData.incomeYear} income year? *`,
      subLabel: [
        'taxable income (excluding any assessable First home super saver released amount)',
        'exempt Australian Government allowances, pensions and payments',
        'target foreign income',
        'reportable fringe benefits total',
        'total net investment loss, and',
        'reportable superannuation contributions'
      ],
      required: true,
      options: [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' }
      ]
    }
  ]
},
  
    residencyStatus: { // Corresponds to target_residency_status
      condition: (formData) => formData.saptoEligible === 'no' && formData.govAllowance === 'no',
      fields: [
        {
          type: 'radio',
          name: 'residencyStatus',
          label: 'What was your residency status for tax purposes for the full income year? *',
          required: true,
          options: [
            { value: 'fullResident', label: 'Australian resident for the full year' },
            { value: 'fullForeignResident', label: 'Foreign resident for the full year' }, // Backend uses "fullForeignResident"
            { value: 'partResident', label: 'Part-year Australian resident' }, // Backend uses "partResident"
          ]
        }
      ]
    },
    numMonthsResident: { // Corresponds to target_num_months_resident
      condition: (formData) => formData.saptoEligible === 'no' &&
                             formData.govAllowance === 'no' &&
                             formData.residencyStatus === 'partResident',
      fields: [
        {
          type: 'select',
          name: 'numMonthsResident',
          label: 'For how many months were you an Australian resident for tax purposes? *',
          required: true,
          options: [
            { value: '', label: 'Select number of months' },
            ...Array.from({ length: 11 }, (_, i) => ({ // Generates 1 to 11. Backend uses "ddl-numMthsAusResident"
              value: String(i + 1),
              label: `${i + 1} month${i === 0 ? '' : 's'}`
            }))
          ]
        }
      ]
    },
    eighteenOrOlder: { // Corresponds to target_eighteen_or_older
      condition: (formData) =>
        formData.saptoEligible === 'no' &&
        formData.govAllowance === 'no' &&
        (
          formData.residencyStatus === 'fullResident' ||
          (formData.residencyStatus === 'partResident' && formData.numMonthsResident !== undefined && formData.numMonthsResident !== '')
        ),
      fields: [
        {
          type: 'radio',
          name: 'eighteenOrOlder',
          label: 'Were you 18 years or older on 30 June of the selected income year? *',
          required: true,
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' }
          ]
        }
      ]
    },
    salaryOnly: { // Corresponds to target_salary_only
      condition: (formData) =>
        formData.saptoEligible === 'no' &&
        formData.govAllowance === 'no' &&
        (
          (formData.residencyStatus === 'fullResident' && formData.eighteenOrOlder === 'no') ||
          (formData.residencyStatus === 'partResident' &&
           formData.numMonthsResident !== undefined && formData.numMonthsResident !== '' &&
           formData.eighteenOrOlder === 'no')
        ),
      fields: [
        {
          type: 'radio',
          name: 'salaryOnly',
          label: 'Did you earn income ONLY from salary or wages (excluding pensions, annuities, government payments or compensation)? *',
          required: true,
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' }
          ]
        }
      ]
    },
    incomeAbove18200:{
      condition: (formData) => (formData.saptoEligible === 'no' &&
                             formData.govAllowance === 'no' &&
                             formData.residencyStatus === 'fullResident' &&
                             formData.eighteenOrOlder === 'yes')||formData.salaryOnly==='no',

      fields: [
        {
          type: 'radio',
          name: 'incomeAbove18200',
          label: 'Did you earn income above $18,200 during the selected income year? *',
          required: true,
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' }
          ]
        }
      ]
    },

  

    taxWithheldGovNo: { // Corresponds to target_tax_withheld
      condition: (formData) =>
        // SAPTO=YES path: after overRebateIncomeLimit is answered
      (formData.incomeAbove18200 === 'no') ,
      fields: [
        {
          type: 'radio',
          name: 'taxWithheldGovNo',
          label: 'Did you have tax withheld from any payments made to you during the income year (e.g., from salary, wages, investment income)? *',
          required: true,
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' }
          ]
        }
      ]
    },
    paygInstalmentGovNo: { // Corresponds to target_payg_instalment
      condition: (formData) => formData.saptoEligible==='no'&& formData.taxWithheldGovNo === 'no', // Appears if taxWithheld was visible and answered 'no'
      fields: [
        {
          type: 'radio',
          name: 'paygInstalmentGovNo',
          label: 'Did you make Pay As You Go (PAYG) installments during the income year? *',
          required: true,
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' }
          ]
        }
      ]
    },
  businessEventsGovNo: { // Corresponds to target_other_events (array of values e.g. "1","2","3")
      condition: (formData) => formData.paygInstalmentGovNo === 'no',
      fields: [
        {
          type: 'checkboxGroup',
          name: 'businessEventsGovNo',
          label: 'Select the business or investment events that applied to you during the 2021-22 income year *',
          required: true, // As per original schema
          options: [
              { value: '1', label: 'You carried on a business' },
              { value: '2', label: 'You received income from dividends or distributions that exceeded the threshold amount of $18,200.' },
              { value: '3', label: 'The threshold amount is $416 if you were under 18 years of age at the end of the financial year and were entitled to a franking credit.' },
              { value: '4', label: 'You made a loss (including a capital or a non-capital loss on redemption of a traditional security) or you can claim such a loss you made in a previous year' },
              { value: '5', label: 'You were entitled to a distribution from a trust or a partnership that carried on a primary production business' },
              { value: '6', label: 'You qualified as a special professional (sportsperson, artist, inventor) covered by the income averaging rules' },
              { value: 'none', label: 'None of the above' }
    ]
        }
      ]
    },
    superEventsGovNo: { // Corresponds to target_super_events (array of values e.g. "1","2","3")
      condition: (formData) => formData.businessEventsGovNo.includes('none'),
      fields: [
        {
          type: 'checkboxGroup',
          name: 'superEventsGovNo',
          label: 'Did any of the following superannuation related events occur? (Select all that apply)',
          required: true, // As per original schema
          options: [
      { 
        value: '1', 
        label: 'Your concessional contributions to your super exceeded your concessional contributions cap' 
      },
      { 
        value: '2', 
        label: 'Your non-concessional contributions to your super exceeded your non-concessional contributions cap' 
      },
      { 
        value: '3', 
        label: 'You made personal contributions (not including amounts which you are claiming as a deduction) to a complying super fund or retirement savings account and will be eligible to receive a super co-contribution for these contributions',
        tooltip: 'A complying super fund needs to be an Australian super fund at all times during the financial year.'
      },
      { 
        value: '4', 
        label: 'You received an Australian superannuation lump sum that included an untaxed element or it is a superannuation lump sum death benefit paid to you as a non-dependant and you were 60 years old or older' 
      },
      { 
        value: '5', 
        label: 'You received an Australian superannuation lump sum that included a taxed element or untaxed element or it is a superannuation lump sum death benefit paid to you as a non-dependant and you were under 60 years old' 
      },
      { 
        value: 'none', 
        label: 'None of the above' 
      }
    ]
        }
      ]
    },

    otherEventsGovNo: { // Corresponds to target_other_events (array of values e.g. "1","2","3")
    condition: (formData) => formData.superEventsGovNo.includes('none'),
    fields: [
      {
        type: 'checkboxGroup',
        name: 'otherEventsGovNo',
        label: 'Did any of these other specific circumstances apply to you? (Select all that apply)',
        required: true, // As per original schema
        options: [
          { 
            value: '1', 
            label: 'You received a payment summary or an income statement with a reportable fringe benefits amount' 
          },
          { 
            value: '2', 
            label: 'You received a payment summary or an income statement with a reportable employer superannuation contributions amount' 
          },
          { 
            value: '3', 
            label: 'You were entitled to the private health insurance rebate but you did not claim your correct entitlement as a premium reduction, and your spouse (if you had one) is not claiming the rebate for you in their income tax return' 
          },
          { 
            value: '4', 
            label: 'You are entitled to a credit for the foreign resident capital gains withholding paid by the purchaser to the ATO when you sold the property, share or unit' 
          },
          { 
            value: '5', 
            label: 'You were an Australian resident for tax purposes with exempt foreign employment income and $1 or more of other income' 
          },
          { 
            value: 'none', 
            label: 'None of the above' 
          }
        ]
      }
    ]
  },
foreignResidentHelpGovNo: {
condition: (formData) => formData.otherEventsGovNo.includes('none'),
fields: [
  {
    type: 'radio',
    name: 'foreignResidentHelpGovNo',
    label: (formData)=> `Did all of the following circumstances apply to you in the ${formData.incomeYear} income year? *`,
    subLabel: [
      'You were a foreign resident for tax purposes (full or part year)',
      'You had a Higher Education Loan Program, VET Student Loan or Australian Apprenticeship Support Loan debt, and',
      'Your worldwide income was above $11,753'
    ],
  //   tooltip: 'Your worldwide income includes income from all sources, both in Australia and overseas.',
    required: true,
    options: [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'No' }
    ]
  }
]
},
entitledToChildSupportGovNo: { // Corresponds to target_payg_instalment
      condition: (formData) => formData.foreignResidentHelpGovNo === 'yes', // Appears if taxWithheld was visible and answered 'no'
      fields: [
        {
          type: 'radio',
          name: 'entitledToChildSupportGovNo',
          label: (formData)=>`Were you a liable parent or a parent entitled to child support under a child support assessment during the ${formData.incomeYear} income year? *`,
          required: true,
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' }
          ]
        }
      ]
    },
receivedPensionGovNo: { // Corresponds to target_payg_instalment
      condition: (formData) => formData.entitledToChildSupportGovNo === 'yes', // Appears if taxWithheld was visible and answered 'no'
      fields: [
        {
          type: 'radio',
          name: 'receivedPensionGovNo',
          label: (formData)=>`Were you a liable parent or a parent entitled to child support under a child support assessment during the ${formData.incomeYear} income year? *`,
          required: true,
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' }
          ]
        }
      ]
    },
    // Replace the totalMoreThanThreshold placeholder with:
totalMoreThanThresholdGovNo: {
  condition: (formData) => formData.receivedPensionGovNo === 'yes',
  fields: [
    {
      type: 'radio',
      name: 'totalMoreThanThresholdGovNo',
      label: (formData) => `Was the total of all the following payments that you received more than $27,063 for the ${formData.incomeYear} income year? *`,
      subLabel: [
        'taxable income (excluding any assessable First home super saver released amount)',
        'exempt Australian Government allowances, pensions and payments',
        'target foreign income',
        'reportable fringe benefits total',
        'total net investment loss, and',
        'reportable superannuation contributions'
      ],
      required: true,
      options: [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' }
      ]
    }
  ]
},
  
   
  
    // eighteenOrOlder: { // Corresponds to target_eighteen_or_older
    //   condition: (formData) =>
    //     formData.saptoEligible === 'no' &&
    //     formData.govAllowance === 'no' &&
    //     (
    //       formData.residencyStatus === 'fullResident' ||
    //       (formData.residencyStatus === 'partResident' && formData.numMonthsResident !== undefined && formData.numMonthsResident !== '')
    //     ),
    //   fields: [
    //     {
    //       type: 'radio',
    //       name: 'eighteenOrOlder',
    //       label: 'Were you 18 years or older on 30 June of the selected income year? *',
    //       required: true,
    //       options: [
    //         { value: 'yes', label: 'Yes' },
    //         { value: 'no', label: 'No' }
    //       ]
    //     }
    //   ]
    // },
  
    salaryOnly: { // Corresponds to target_salary_only
      condition: (formData) =>
        formData.saptoEligible === 'no' &&
        formData.govAllowance === 'no' &&
        (
          (formData.residencyStatus === 'fullResident' && formData.eighteenOrOlder === 'no') ||
          (formData.residencyStatus === 'partResident' &&
           formData.numMonthsResident !== undefined && formData.numMonthsResident !== '' &&
           formData.eighteenOrOlder === 'no')
        ),
      fields: [
        {
          type: 'radio',
          name: 'salaryOnly',
          label: 'Did you earn income ONLY from salary or wages (excluding pensions, annuities, government payments or compensation)? *',
          required: true,
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' }
          ]
        }
      ]
    },
  
    // overTaxableIncomeThreshold: { // Corresponds to target_over_taxable_income_threshold
    //   condition: (formData) => {
    //       // Condition is met if we are on a path that asks this question.
    //       // This relies on the individual questions (govAllowance, eighteenOrOlder, salaryOnly, residencyStatus)
    //       // having their own correct visibility conditions.
  
    //       // Path 1: SAPTO=no, GovAllowance=yes
    //       if (formData.govAllowance === 'yes' && formData.saptoEligible === 'no') return true;
  
    //       // Path 2: SAPTO=no, GovAllowance=no, ... eighteenOrOlder=yes
    //       if (formData.eighteenOrOlder === 'yes') return true; // eighteenOrOlder's condition implies SAPTO=no, GovAllowance=no, and valid residency path
  
    //       // Path 3: SAPTO=no, GovAllowance=no, ... eighteenOrOlder=no, salaryOnly is answered
    //       if ((formData.salaryOnly !== undefined && formData.salaryOnly !== '') && formData.eighteenOrOlder === 'no') return true; // salaryOnly's condition implies path
  
    //       // Path 4: SAPTO=no, GovAllowance=no, Residency=fullForeignResident
    //       if (formData.saptoEligible === 'no' && formData.govAllowance === 'no' && formData.residencyStatus === 'fullForeignResident') return true;
  
    //       return false;
    //   },
    //   fields: [
    //     {
    //       type: 'radio',
    //       name: 'overTaxableIncomeThreshold',
    //       label: 'Was your taxable income over the relevant tax-free threshold? *',
    //       required: true,
    //       options: [
    //         { value: 'yes', label: 'Yes' },
    //         { value: 'no', label: 'No' }
    //       ]
    //     }
    //   ]
    // },
  
    // ---- COMMON QUESTIONS (conditionally appearing in various flows) ----
    
  
   
  
    // ---- CHECKBOXES & SUBSEQUENT QUESTIONS (if PAYG Instalment = 'no') ----
    // Backend references: _handle_checkboxes, _handle_post_checkbox_steps
    // businessEvents: { // Corresponds to target_business_events (array of values e.g. "1","2","3","4")
    //   condition: (formData) => formData.paygInstalment === 'no',
    //   fields: [
    //     {
    //       type: 'checkboxGroup',
    //       name: 'businessEvents',
    //       label: 'Did any of the following business or investment related events occur during the income year? (Select all that apply)',
    //       required: true, // As per original schema; backend handles empty list.
    //       options: [ // Values must match chk-businessEvent{value} in backend
    //         { value: '1', label: 'You were entitled to claim GST credits for fuel (fuel tax credits).' },
    //         { value: '2', label: 'You received income from a business or investments (e.g. rent, dividends, interest).' },
    //         { value: '3', label: 'You need to claim deductions related to a business or investments.' },
    //         { value: '4', label: 'You made a capital gain or loss from selling assets (e.g. shares, property).' }
    //       ]
    //     }
    //   ]
    // },
  
   
  
    
  
    // helpAaslDebt: { // Corresponds to target_help_aasl_debt
    //   condition: (formData) => formData.paygInstalment === 'no',
    //   fields: [
    //     {
    //       type: 'radio',
    //       name: 'helpAaslDebt',
    //       label: 'Do you have a Higher Education Loan Program (HELP), VET Student Loan (VSL) or Trade Support Loan (TSL) debt? *',
    //       required: true,
    //       options: [
    //         { value: 'yes', label: 'Yes' },
    //         { value: 'no', label: 'No' }
    //       ]
    //     }
    //   ]
    // },
  
    childSupport: { // Renamed from childSupportAsst; Corresponds to target_child_support
      condition: (formData) => formData.paygInstalment === 'no' && formData.helpAaslDebt === 'no',
      fields: [
        {
          type: 'radio',
          name: 'childSupport',
          label: 'Were you assessed by Child Support (Services Australia) or did you have child support obligations or entitlements during the year? *',
          required: true,
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' }
          ]
        }
      ]
    },
  
    govPayments: { // Renamed from australianGovernmentPayments; Corresponds to target_gov_payments
      condition: (formData) => formData.paygInstalment === 'no' &&
                             formData.helpAaslDebt === 'no' &&
                             formData.childSupport === 'yes',
      fields: [
        {
          type: 'radio',
          name: 'govPayments',
          label: 'Did you receive any Australian Government payments (like JobSeeker, Youth Allowance, Parenting Payment) that were not purely for family tax benefit or child care subsidy? *',
          required: true,
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' }
          ]
        }
      ]
    },
  
    overChildSupportThreshold: { // Corresponds to target_over_child_support_threshold
      condition: (formData) => formData.paygInstalment === 'no' &&
                             formData.helpAaslDebt === 'no' &&
                             formData.childSupport === 'yes' &&
                             formData.govPayments === 'yes',
      fields: [
        {
          type: 'radio',
          name: 'overChildSupportThreshold',
          label: 'Was your income (and your partner\'s if applicable) above the relevant child support income threshold? *',
          required: true,
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' }
          ]
        }
      ]
    }
  };

const LodgeTaxCalculator = () => {
  const [formData, setFormData] = useState({
    incomeYear: '',
    pensionEligiblity: '',
    maritalStatus: '',
    overRebateIncomeLimit: '',
    taxWithheld:'',
    governmentAllowance: '',
    residencyStatus: '',
    numMonthsResident: '',
    eighteenOrOlder: '',
    salaryOnly: '',
    paygInstalment: '',
    overTaxableIncomeThreshold: '',
    businessEvents: [],
    superEvents: [],
    otherEvents: [],
    foreignResidentHelp:'',
    entitledToChildSupport:'',
    receivedPension:'',
    totalMoreThanThreshold:'',
    helpAaslDebt: '',
    childSupportAsst: '',
    australianGovernmentPayments: '',
    overChildSupportThreshold: '',
    incomeAboveThreshold:'',
    taxWithheldSaptoNo:'',
    paygInstalmentSaptoNo:'',
    businessEventsSaptoNo: [],
    superEventsSaptoNo: [],
    otherEventsSaptoNo: [],
    foreignResidentHelpSaptoNo:'',
    entitledToChildSupportSaptoNo:'',
    receivedPensionSaptoNo:'',
    totalMoreThanThresholdSaptoNo:'',
    incomeAbove18200:'',
    taxWithheldGovNo:'',
    paygInstalmentGovNo:'',
    businessEventsGovNo: [],
    superEventsGovNo: [],
    otherEventsGovNo: [],
    foreignResidentHelpGovNo:'',
    entitledToChildSupportGovNo:'',
    receivedPensionGovNo:'',
    totalMoreThanThresholdGovNo:'',

  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value
    }));
  };

  const renderField = (field) => {
    if (field.condition && !field.condition(formData)) return null;

    switch (field.type) {
      case 'select':
        return (
          <div key={field.name} className="form-group">
            <label htmlFor={field.name}>{field.label}</label>
            <select
              id={field.name}
              name={field.name}
              value={formData[field.name]}
              onChange={handleChange}
              required={field.required}
            >
              {field.options.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        );

        case 'radio':
            return (
              <div key={field.name} className="form-group">
                <label>{typeof field.label === 'function' ? field.label(formData) : field.label}</label>
                {field.subLabel && (
                  <ul className="sub-label">
                    {field.subLabel.map((text, index) => (
                      <li key={index}>{text}</li>
                    ))}
                  </ul>
                )}
                <div className="radio-group">
                  {field.options.map(opt => (
                    <div key={opt.value} className="radio-option">
                      <input
                        type="radio"
                        id={`${field.name}-${opt.value}`}
                        name={field.name}
                        value={opt.value}
                        checked={formData[field.name] === opt.value}
                        onChange={handleChange}
                        required={field.required}
                      />
                      <label htmlFor={`${field.name}-${opt.value}`}>
                        {opt.label}
                      </label>
                    </div>
                  ))}
                </div>
                {field.tooltip && (
                  <div className="tooltip">{field.tooltip}</div>
                )}
              </div>
            );

      case 'currency':
        return (
          <div key={field.name} className="form-group">
            <label htmlFor={field.name}>{field.label}</label>
            <div className="input-with-prefix">
              <span className="currency-symbol">{field.prefix}</span>
              <input
                type="number"
                id={field.name}
                name={field.name}
                value={formData[field.name]}
                onChange={handleChange}
                required={field.required}
                step="0.01"
              />
            </div>
          </div>
        );

      case 'number':
        return (
          <div key={field.name} className="form-group">
            <label htmlFor={field.name}>{field.label}</label>
            <div className="input-with-suffix">
              <input
                type="number"
                id={field.name}
                name={field.name}
                value={formData[field.name]}
                onChange={handleChange}
                required={field.required}
              />
              {field.suffix && <span className="unit">{field.suffix}</span>}
            </div>
          </div>
        );

      case 'checkboxGroup':
        return (
          <div key={field.name} className="form-group">
            <label>{field.label}</label>
            <div className="checkbox-group">
              {field.options.map(opt => (
                <div key={opt.value} className="checkbox-option">
                  <input
                    type="checkbox"
                    id={`${field.name}-${opt.value}`}
                    name={field.name}
                    value={opt.value}
                    checked={formData[field.name]?.includes(opt.value)}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData(prev => {
                        // Handle 'None of the above' option
                        if (value === 'none') {  // Assuming '7' is 'None of the above'
                          return {
                            ...prev,
                            [field.name]: e.target.checked ? ['none'] : []
                          };
                        }
                        
                        // Handle other options
                        const currentValues = prev[field.name] || [];
                        if (e.target.checked) {
                          // Remove 'None of the above' if it was previously selected
                          const newValues = currentValues.filter(v => v !== 'none');
                          return {
                            ...prev,
                            [field.name]: [...newValues, value]
                          };
                        } else {
                          return {
                            ...prev,
                            [field.name]: currentValues.filter(v => v !== value)
                          };
                        }
                      });
                    }}
                    required={field.required && formData[field.name]?.length === 0}
                  />
                  <label htmlFor={`${field.name}-${opt.value}`}>
                    {opt.label}
                  </label>
                </div>
              ))}
            </div>
          </div>
        );
        default:
          <></>
      // Add other field types as needed
    }
  };

  const renderSection = (section, sectionKey) => {
    if (section.condition && !section.condition(formData)) return null;

    return (
      <div key={sectionKey} className="form-section">
        <h2>{section.title}</h2>
        {section.fields.map(field => renderField(field))}
      </div>
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      // const payload = {
      //   target_income_year: formData.incomeYear || null,
      //   target_sapto_eligible: formData.saptoEligible||null,
      //   target_marital_status: formData.maritalStatus || null,
      //   target_over_rebate_limit: formData.overRebateIncomeLimit === 'yes' || null,
      //   target_tax_withheld: formData.taxWithheld === 'yes' || formData.taxWithheldSaptoNo === 'yes' || formData.taxWithheldGovNo === 'yes' || null,
      //   target_payg_instalments: formData.paygInstalment === 'yes' || formData.paygInstalmentSaptoNo === 'yes' || formData.paygInstalmentGovNo === 'yes' || null,
      //   target_business_events: [
      //     ...formData.businessEvents.filter(val => val !== 'none'),
      //     ...formData.businessEventsSaptoNo.filter(val => val !== 'none'),
      //     ...formData.businessEventsGovNo.filter(val => val !== 'none')
      //   ],
      //   target_super_events: [
      //     ...formData.superEvents.filter(val => val !== 'none'),
      //     ...formData.superEventsSaptoNo.filter(val => val !== 'none'),
      //     ...formData.superEventsGovNo.filter(val => val !== 'none')
      //   ],
      //   target_other_events: [
      //     ...formData.otherEvents.filter(val => val !== 'none'),
      //     ...formData.otherEventsSaptoNo.filter(val => val !== 'none'),
      //     ...formData.otherEventsGovNo.filter(val => val !== 'none')
      //   ],
      //   target_gov_allowance: formData.govAllowance || null,
      //   target_residency_status: formData.residencyStatus || null,
      //   target_months_resident: Number(formData.numMonthsResident) || null,
      //   target_eighteen_or_older: formData.eighteenOrOlder || null,
      //   target_salary_only: formData.salaryOnly === 'yes' || null,
      //   target_over_taxable_threshold: formData.overTaxableIncomeThreshold === 'yes' || formData.incomeAboveThreshold === 'yes' || null,
      //   target_help_aasl_debt: formData.helpAaslDebt === 'yes' || formData.foreignResidentHelp === 'yes' || formData.foreignResidentHelpSaptoNo === 'yes' || formData.foreignResidentHelpGovNo === 'yes' || null,
      //   target_child_support: formData.childSupportAsst === 'yes' || formData.entitledToChildSupport === 'yes' || formData.entitledToChildSupportSaptoNo === 'yes' || formData.entitledToChildSupportGovNo === 'yes' || null,
      //   target_gov_payments: formData.australianGovernmentPayments === 'yes' || formData.receivedPension === 'yes' || formData.receivedPensionSaptoNo === 'yes' || formData.receivedPensionGovNo === 'yes' || null,
      //   target_over_child_support_threshold: formData.overChildSupportThreshold === 'yes' || formData.totalMoreThanThreshold === 'yes' || formData.totalMoreThanThresholdSaptoNo === 'yes' || formData.totalMoreThanThresholdGovNo === 'yes' || null
      // };

      const payload = {
        target_income_year: formData.incomeYear || null,
        target_sapto_eligible: formData.saptoEligible ||null,
        target_marital_status: formData.maritalStatus || null,
        target_over_rebate_limit: formData.overRebateIncomeLimit === 'yes' ? 'yes' : 'no',
        target_gov_allowance: formData.governmentAllowance === 'yes' ? 'yes' : 'no',
        target_over_taxable_income_threshold: formData.incomeAbove18200 === 'yes' ? 'yes' : 'no',
        target_residency_status: formData.residencyStatus || null,
        target_num_months_resident: Number(formData.numMonthsResident) || 0,
        target_eighteen_or_older: formData.eighteenOrOlder === 'yes' ? 'yes' : 'no',
        target_salary_only: formData.salaryOnly === 'yes' ? 'yes' : 'no',
        target_tax_withheld: (
          formData.taxWithheld === 'yes' || 
          formData.taxWithheldSaptoNo === 'yes' || 
          formData.taxWithheldGovNo === 'yes'
        ) ? 'yes' : 'no',
        target_payg_instalment: (
          formData.paygInstalment === 'yes' || 
          formData.paygInstalmentSaptoNo === 'yes' || 
          formData.paygInstalmentGovNo === 'yes'
        ) ? 'yes' : 'no',
        target_business_events: [
          ...(formData.businessEvents.includes('none') || 
             formData.businessEventsSaptoNo.includes('none') || 
             formData.businessEventsGovNo.includes('none')) ? ['NoneOfAbove'] : 
          [
            ...formData.businessEvents.filter(val => val !== 'none'),
            ...formData.businessEventsSaptoNo.filter(val => val !== 'none'),
            ...formData.businessEventsGovNo.filter(val => val !== 'none')
          ]
        ],
        target_super_events: [
          ...(formData.superEvents.includes('none') || 
             formData.superEventsSaptoNo.includes('none') || 
             formData.superEventsGovNo.includes('none')) ? ['NoneOfAbove'] : 
          [
            ...formData.superEvents.filter(val => val !== 'none'),
            ...formData.superEventsSaptoNo.filter(val => val !== 'none'),
            ...formData.superEventsGovNo.filter(val => val !== 'none')
          ]
        ],
        target_other_events: [
          ...(formData.otherEvents.includes('none') || 
             formData.otherEventsSaptoNo.includes('none') || 
             formData.otherEventsGovNo.includes('none')) ? ['NoneOfAbove'] : 
          [
            ...formData.otherEvents.filter(val => val !== 'none'),
            ...formData.otherEventsSaptoNo.filter(val => val !== 'none'),
            ...formData.otherEventsGovNo.filter(val => val !== 'none')
          ]
        ],
        target_help_aasl_debt: (
          formData.helpAaslDebt === 'yes' || 
          formData.foreignResidentHelp === 'yes' || 
          formData.foreignResidentHelpSaptoNo === 'yes' || 
          formData.foreignResidentHelpGovNo === 'yes'
        ) ? 'yes' : 'no',
        target_child_support: (
          formData.childSupportAsst === 'yes' || 
          formData.entitledToChildSupport === 'yes' || 
          formData.entitledToChildSupportSaptoNo === 'yes' || 
          formData.entitledToChildSupportGovNo === 'yes'
        ) ? 'yes' : 'no',
        target_gov_payments: (
          formData.australianGovernmentPayments === 'yes' || 
          formData.receivedPension === 'yes' || 
          formData.receivedPensionSaptoNo === 'yes' || 
          formData.receivedPensionGovNo === 'yes'
        ) ? 'yes' : 'no',
        target_over_child_support_threshold: (
          formData.overChildSupportThreshold === 'yes' || 
          formData.totalMoreThanThreshold === 'yes' || 
          formData.totalMoreThanThresholdSaptoNo === 'yes' || 
          formData.totalMoreThanThresholdGovNo === 'yes'
        ) ? 'yes' : 'no'
      };

      const response = await axios.post(`${API_BASE_URL}/ato_calculator/do_i_need_to_lodge`, payload);
      setResult(response?.data?.details_text);
    } catch (err) {
      setError(err.response?.data?.message || 'An error occurred while processing your request');
    } finally {
      setLoading(false);
    }
};

  return (
    <div className="form-container">
      <h1>Do I need to lodge a tax return?</h1>
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}
      
      <form onSubmit={handleSubmit}>
        {Object.entries(formSchema).map(([key, section]) => 
          renderSection(section, key)
        )}
        <button 
          type="submit" 
          className="btn btn-primary"
          disabled={loading}
        >
          {loading ? 'Calculating...' : 'Calculate'}
        </button>
      </form>

      {loading && (
        <div className="loader">
        </div>
      )}

      {result && (
        <div className="result-container">
          <h2>Results</h2>
          <div className="result-content">
            <p><strong>Residency Status:</strong> {result}</p>
            {result.explanation && (
              {/* <p><strong>Explanation:</strong> {result.explanation}</p> */}
            )}
            {/* {result.lodgment_required !== undefined && (
              <p><strong>Lodgment Required:</strong> {result.lodgment_required ? 'Yes' : 'No'}</p>
            )}
            {result.lodgment_explanation && (
              <p><strong>Lodgment Details:</strong> {result.lodgment_explanation}</p>
            )} */}
          </div>
        </div>
      )}
    </div>
  );
};

export default LodgeTaxCalculator;